/**
 * useCreditNotes Hook
 *
 * CRUD operations for credit notes via the credit-notes Edge Function.
 * Supports creating, listing, getting, and deleting credit notes.
 *
 * User Story: US-REC-001 - Invoice Reconciliation
 */

import { useState, useEffect, useCallback } from 'react'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

/**
 * Valid credit note types
 */
export const CREDIT_NOTE_TYPES = [
  { value: 'write_off', label: 'Write Off' },
  { value: 'adjustment', label: 'Adjustment' },
  { value: 'refund', label: 'Refund' },
  { value: 'goodwill', label: 'Goodwill' },
  { value: 'legacy_settlement', label: 'Legacy Settlement' }
]

/**
 * Hook to manage credit notes for an invoice or customer
 * @param {Object} options - Filter options
 * @param {string} options.invoiceId - Filter by invoice ID
 * @param {string} options.customerId - Filter by customer ID
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @returns {Object} { creditNotes, isLoading, error, createCreditNote, deleteCreditNote, refetch }
 */
export function useCreditNotes(options = {}) {
  const [creditNotes, setCreditNotes] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [totalCredited, setTotalCredited] = useState(0)

  const { invoiceId, customerId, autoFetch = true } = options

  /**
   * Fetch credit notes from the Edge Function
   */
  const fetchCreditNotes = useCallback(async () => {
    // Skip fetch if no filters provided
    if (!invoiceId && !customerId) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Use get_by_invoice for invoice-specific queries, otherwise list
      const action = invoiceId ? 'get_by_invoice' : 'list'
      const payload = invoiceId
        ? { action, invoice_id: invoiceId }
        : { action, customer_id: customerId }

      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/credit-notes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify(payload)
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP error: ${response.status}`)
      }

      const result = await response.json()

      if (result.error) {
        throw new Error(result.message || 'Failed to fetch credit notes')
      }

      // Handle different response formats
      if (invoiceId) {
        setCreditNotes(result.credit_notes || [])
        setTotalCredited(result.total_credited || 0)
      } else {
        setCreditNotes(result.credit_notes || [])
        setTotalCredited(
          (result.credit_notes || []).reduce((sum, cn) => sum + parseFloat(cn.amount), 0)
        )
      }
    } catch (err) {
      console.error('[useCreditNotes] Error fetching credit notes:', err)
      setError(err.message)
      setCreditNotes([])
      setTotalCredited(0)
    } finally {
      setIsLoading(false)
    }
  }, [invoiceId, customerId])

  /**
   * Create a new credit note
   * @param {Object} data - Credit note data
   * @param {string} data.customer_id - Customer ID
   * @param {string} data.invoice_id - Invoice ID (optional for account-level credits)
   * @param {number} data.amount - Credit amount
   * @param {string} data.reason - Reason for credit (required)
   * @param {string} data.type - Credit note type (write_off, adjustment, refund, goodwill)
   * @param {string} data.date - Date (YYYY-MM-DD, defaults to today)
   * @param {string} data.notes - Additional notes (optional)
   * @returns {Promise<Object>} Created credit note
   */
  const createCreditNote = useCallback(async (data) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/credit-notes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            action: 'create',
            ...data
          })
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP error: ${response.status}`)
      }

      const result = await response.json()

      if (result.error) {
        throw new Error(result.message || 'Failed to create credit note')
      }

      // Refetch credit notes to update the list
      await fetchCreditNotes()

      return result
    } catch (err) {
      console.error('[useCreditNotes] Error creating credit note:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchCreditNotes])

  /**
   * Retry syncing a credit note to Xero
   * @param {string} id - Credit note ID to retry sync
   * @returns {Promise<Object>} Retry result with updated sync status
   */
  const retrySyncToXero = useCallback(async (id) => {
    setError(null)

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/xero-credit-notes/retry/${id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY
          }
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP error: ${response.status}`)
      }

      const result = await response.json()

      if (result.error) {
        throw new Error(result.message || 'Failed to retry sync')
      }

      // Refetch credit notes to update the list with new sync status
      await fetchCreditNotes()

      return result
    } catch (err) {
      console.error('[useCreditNotes] Error retrying sync:', err)
      setError(err.message)
      throw err
    }
  }, [fetchCreditNotes])

  /**
   * Delete a credit note (only if not synced to Xero)
   * @param {string} id - Credit note ID to delete
   * @returns {Promise<Object>} Deletion result
   */
  const deleteCreditNote = useCallback(async (id) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/credit-notes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            action: 'delete',
            id
          })
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP error: ${response.status}`)
      }

      const result = await response.json()

      if (result.error) {
        throw new Error(result.message || 'Failed to delete credit note')
      }

      // Refetch credit notes to update the list
      await fetchCreditNotes()

      return result
    } catch (err) {
      console.error('[useCreditNotes] Error deleting credit note:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchCreditNotes])

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && (invoiceId || customerId)) {
      fetchCreditNotes()
    }
  }, [autoFetch, invoiceId, customerId, fetchCreditNotes])

  return {
    creditNotes,
    totalCredited,
    isLoading,
    error,
    createCreditNote,
    deleteCreditNote,
    retrySyncToXero,
    refetch: fetchCreditNotes
  }
}

export default useCreditNotes
